import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import { useGeneratePersonas } from '@/hooks/useEvaluator';
import { useOrganization } from '@/hooks/useOrganization';
import { toast } from '@/hooks/use-toast';
import type { PopulationKind } from '@/types/evaluator';
import { cn } from '@/lib/utils';

interface GeneratePersonasDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  onSuccess?: () => void;
}

export function GeneratePersonasDialog({
  open,
  onOpenChange,
  organizationId,
  onSuccess,
}: GeneratePersonasDialogProps) {
  const { currentOrganization } = useOrganization();
  const [datasetName, setDatasetName] = useState('');
  const [count, setCount] = useState(5);
  const [populationKind, setPopulationKind] = useState<PopulationKind>('representative');
  const [description, setDescription] = useState('');
  const generateMutation = useGeneratePersonas();

  const reset = () => {
    setDatasetName('');
    setCount(5);
    setPopulationKind('representative');
    setDescription('');
  };

  const trimmedName = datasetName.trim();
  const trimmedDescription = description.trim();
  const isCountValid = Number.isFinite(count) && count >= 1 && count <= 20;
  const isDescriptionValid = populationKind !== 'targeted' || trimmedDescription.length > 0;
  const canSubmit = trimmedName.length > 0 && isCountValid && isDescriptionValid;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      await generateMutation.mutateAsync({
        dataset_name: trimmedName,
        num_personas: count,
        organization_id: organizationId,
        population_kind: populationKind,
        description: populationKind === 'targeted' ? trimmedDescription : undefined,
      });
      toast({ title: 'Success', description: `Generated ${count} personas in "${trimmedName}".` });
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to generate personas.',
        variant: 'destructive',
      });
    }
  };

  const handleCountChange = (raw: string) => {
    if (raw === '') {
      setCount(Number.NaN);
      return;
    }
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    setCount(Math.max(1, Math.min(20, parsed)));
  };

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) reset();
  };

  const isSubmitting = generateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center text-base">
            Create Population
            <HelpTooltip text="A reusable group of personas you can run simulations against." />
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="grid gap-5">
          <Field label="Name" htmlFor="population-name">
            <Input
              id="population-name"
              value={datasetName}
              onChange={(e) => setDatasetName(e.target.value)}
              placeholder={`e.g. ${currentOrganization?.name || 'Acme'} Customers`}
              disabled={isSubmitting}
            />
          </Field>

          <Field label="Type">
            <div className="grid grid-cols-2 gap-2">
              <KindCard
                selected={populationKind === 'representative'}
                onSelect={() => setPopulationKind('representative')}
                title="Representative"
                tooltip="A varied, realistic spread of buyers across roles, industries, and buying stages."
              />
              <KindCard
                selected={populationKind === 'targeted'}
                onSelect={() => setPopulationKind('targeted')}
                title="Targeted"
                tooltip="A specific segment you describe — industry, role, attitude, or buying stage."
              />
            </div>
          </Field>

          {populationKind === 'targeted' && (
            <Field
              label="Segment"
              htmlFor="population-description"
              hint="Industry, role, buying stage, or attitudes."
            >
              <Textarea
                id="population-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Mid-market RevOps leaders evaluating point solutions; skeptical of AI; budget-constrained."
                rows={3}
                maxLength={2000}
                disabled={isSubmitting}
              />
            </Field>
          )}

          <Field label="Personas" htmlFor="persona-count" inline>
            <Input
              id="persona-count"
              type="number"
              inputMode="numeric"
              min={1}
              max={20}
              value={Number.isFinite(count) ? count : ''}
              onChange={(e) => handleCountChange(e.target.value)}
              onBlur={() => {
                if (!Number.isFinite(count)) setCount(1);
              }}
              disabled={isSubmitting}
              className="w-20 text-right tabular-nums"
            />
            <span className="text-xs text-muted-foreground">1–20</span>
          </Field>

          <DialogFooter className="mt-1">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !canSubmit}>
              {isSubmitting ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  inline,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  inline?: boolean;
  children: React.ReactNode;
}) {
  if (inline) {
    return (
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
          {label}
        </Label>
        <div className="flex items-center gap-2">{children}</div>
      </div>
    );
  }
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
        {label}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function KindCard({
  selected,
  onSelect,
  title,
  tooltip,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  tooltip: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2.5 transition-colors',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/40'
          : 'border-border bg-background hover:bg-muted/40',
      )}
    >
      <span className="text-sm font-medium text-foreground">{title}</span>
      <HelpTooltip text={tooltip} />
    </button>
  );
}
